import { formatHumanName, MedplumClient, normalizeErrorString, resolveId } from "@medplum/core";
import type { Invoice, Patient } from "@medplum/fhirtypes";

import chunk from "lodash/chunk";

import {
  getInvoiceDueFromLabel,
  getStatementStatus,
} from "@/billing/components/StatementsTab/utils/statements";
import { filterStatements } from "@/billing/components/StatementsTab/utils/statements-filter";
import { EMPTY_VALUE } from "@/billing/constants/empty-value";
import { LEDGER_IDENTIFIER_CHUNK_SIZE } from "@/billing/constants/ledger-search";
import { NO_STATEMENT_POSTINGS } from "@/billing/constants/statement-postings";
import {
  ARCHIVED_STATEMENT_TAG_QUERY,
  DELETED_STATEMENT_TAG_QUERY,
} from "@/billing/constants/statements";
import { searchLedgerPaymentReconciliations } from "@/billing/services/search-ledger-payment-reconciliations";
import {
  LoadOpenStatementsResult,
  OpenStatementsFilters,
  OpenStatementsRow,
} from "@/billing/types/open-statements";
import type { StatementPostings } from "@/billing/types/statement-postings";
import { resolveBalance } from "@/billing/utils/invoice-balance";
import { statementNumberOf } from "@/billing/utils/invoice-statement-number";
import { sumPostingsByStatement } from "@/billing/utils/ledger-postings";
import { getInvoiceServicePayerCode } from "@/billing/utils/service-payer-code";
import { getPatientMrn } from "@/clients/utils/mrn";
import { VOID_INVOICE_STATUSES } from "@/constants/billing";
import { QuiltedHealthIdentifierSystems } from "@/fhir/enums/quilted-health";
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { joinReferenceString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

/** Maximum Patient `_id` searches in flight at once. */
const PATIENT_SEARCH_CONCURRENCY = 10;

/** Void statuses as a comma list, for the `status:not` search modifier. */
const VOID_INVOICE_STATUS_QUERY = Array.from(VOID_INVOICE_STATUSES).join(",");

/**
 * Matches any Invoice carrying a billing-identifier, whatever its value. A bare `system|` token is
 * the FHIR way to say "has an identifier in this system", which is what selects statements the
 * generation bot produced from Invoices written by anything else.
 */
const BILLING_IDENTIFIER_QUERY = `${QuiltedHealthIdentifierSystems.BillingIdentifier}|`;

/**
 * Every practice statement that could still be open: carrying a billing-identifier, and not
 * archived, soft-deleted or void.
 *
 * Each condition is a search modifier rather than a pass over the results, so an archived statement
 * is never returned to be filtered out (criterion 3) — the same approach the client chart's
 * `fetchStatementsForPatient` takes for its Active scope.
 */
async function readPracticeStatements(
  medplum: MedplumClient,
  practiceId: string,
): Promise<Invoice[]> {
  const result = await fetchAllPaginatedResources<Invoice>(medplum, "Invoice", {
    issuer: joinReferenceString("Organization", practiceId),
    identifier: BILLING_IDENTIFIER_QUERY,
    "status:not": VOID_INVOICE_STATUS_QUERY,
    "_tag:not": `${ARCHIVED_STATEMENT_TAG_QUERY},${DELETED_STATEMENT_TAG_QUERY}`,
  });

  if (!result.success) {
    throw new Error(
      `Failed to read statements for Organization/${practiceId}: ${normalizeErrorString(result.error)}`,
    );
  }

  return result.data;
}

/**
 * Reads the statements' subject Patients, keyed by patient id, in batched `_id` searches run a few
 * at a time. A patient the user cannot read is not returned, so its row shows no name. A failed
 * search rejects, so the load fails instead of showing every name as missing.
 */
async function readPatients(
  medplum: MedplumClient,
  clientIds: string[],
): Promise<Map<string, Patient>> {
  const idChunks = chunk([...new Set(clientIds)], LEDGER_IDENTIFIER_CHUNK_SIZE);
  const pages = await processWithConcurrencyLimit(
    idChunks,
    async (ids) => {
      const result = await fetchAllPaginatedResources<Patient>(
        medplum,
        "Patient",
        { _id: ids.join(",") },
        { throwOnPageError: true },
      );
      if (!result.success) throw result.error;
      return result.data;
    },
    PATIENT_SEARCH_CONCURRENCY,
  );

  return pages.flat().reduce<Map<string, Patient>>((patientsById, patient) => {
    if (patient.id) patientsById.set(patient.id, patient);
    return patientsById;
  }, new Map());
}

/**
 * Sums what has been posted against each statement, including a statement moved back to Unsent
 * after a posting. The search follows every page and rejects when one fails, so the load fails
 * instead of showing a statement as having nothing posted.
 */
async function readPostings(
  medplum: MedplumClient,
  invoices: Invoice[],
): Promise<Map<string, StatementPostings>> {
  const paymentReconciliations = await searchLedgerPaymentReconciliations(medplum, invoices);
  return sumPostingsByStatement(paymentReconciliations);
}

/** Builds one table row from a statement and the patients and postings prefetched for the practice. */
function buildRow(
  invoice: Invoice,
  patientsById: Map<string, Patient>,
  postingsByStatement: Map<string, StatementPostings>,
): OpenStatementsRow | null {
  const clientId = resolveId(invoice.subject);
  if (!invoice.id || !clientId) return null;

  const patient = patientsById.get(clientId);
  const statementNumber = statementNumberOf(invoice);
  const postings =
    (statementNumber && postingsByStatement.get(statementNumber)) || NO_STATEMENT_POSTINGS;

  return {
    id: invoice.id,
    clientId,
    clientName: formatHumanName(patient?.name?.[0] ?? {}) || EMPTY_VALUE,
    medicalRecordNumber: (patient && getPatientMrn(patient)) || EMPTY_VALUE,
    serviceDate: invoice.date,
    charges: invoice.totalNet?.value ?? invoice.totalGross?.value,
    dueFrom: getInvoiceDueFromLabel(invoice),
    // No lifecycle tag and a `draft` status read back as Unsent, which is criterion 1: a statement
    // nobody has set a status on has not been sent yet.
    status: getStatementStatus(invoice),
    servicePayerCode: getInvoiceServicePayerCode(invoice),
    payerReference: invoice.recipient,
    payments: postings.payments,
    adjustments: postings.adjustments,
    balance: resolveBalance(invoice),
  };
}

/**
 * A statement belongs on the dashboard while money is still owed on it. A settled statement
 * (balance 0) drops off; a credit balance stays, because an overpayment is an open item the biller
 * still has to resolve. A balance that could not be determined stays too — hiding it would lose the
 * statement.
 */
function hasOpenBalance(invoice: Invoice): boolean {
  return resolveBalance(invoice) !== 0;
}

/**
 * Loads the practice's open statements in no particular order. The table owns ordering, so it
 * sorts what it receives — see `sortOpenStatementsTableRows`.
 *
 * Three rules from the design shape what appears:
 *  1. A statement with no status set reads as Unsent — `getStatementStatus` already does this.
 *  2. A statement whose remaining balance is $0.00 is settled and leaves the dashboard. Balances
 *     above and below zero both stay.
 *  3. A statement archived in the client chart never appears. Soft-deleted and void statements are
 *     excluded on the same grounds.
 *
 * Payments and Adjustments are the sums posted against each statement, read with the same search
 * and the same sums as the client Ledger. A failed read of those postings fails the load.
 *
 * `filters` narrows by badge status through `filterStatements`, the same pass the Statements tab
 * uses. It runs client-side rather than in the search on purpose: the badge is derived from
 * `Invoice.status`, the lifecycle `meta.tag` and the service-payer extension together, and the
 * payer half is not a searchable param — so a `_filter` query cannot express the insurance-only
 * `Accepted` rollup and would return rows whose badge disagrees with the selected status. An empty
 * (or omitted) status list is the unfiltered "All" view.
 */
export async function loadOpenStatements(
  medplum: MedplumClient,
  practiceId: string,
  filters?: OpenStatementsFilters,
): Promise<LoadOpenStatementsResult> {
  if (!practiceId.trim()) {
    return { ok: false, reason: "missing_practice_id" };
  }

  try {
    const statements = await readPracticeStatements(medplum, practiceId);
    // Archived and soft-deleted statements are already excluded by the search, so both scope flags
    // are off here and `filterStatements` narrows by status alone.
    const openInvoices = filterStatements(statements, {
      statuses: filters?.statuses ?? [],
      archived: false,
      deleted: false,
    }).filter(hasOpenBalance);

    const clientIds = openInvoices.reduce<string[]>((ids, invoice) => {
      const clientId = resolveId(invoice.subject);
      if (clientId) ids.push(clientId);
      return ids;
    }, []);

    const [patientsById, postingsByStatement] = await Promise.all([
      readPatients(medplum, clientIds),
      readPostings(medplum, openInvoices),
    ]);

    const rows = openInvoices.reduce<OpenStatementsRow[]>((openRows, invoice) => {
      const row = buildRow(invoice, patientsById, postingsByStatement);
      if (row) openRows.push(row);
      return openRows;
    }, []);

    return { ok: true, data: rows };
  } catch (error) {
    return { ok: false, reason: "load_failed", details: normalizeErrorString(error) };
  }
}
